<template>
  <div class="news-manage-page my-3">
    <div class="page-head card shadow-sm border-0 mb-3">
      <div class="card-body d-flex flex-wrap align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <span class="head-icon"><i class="bi bi-megaphone-fill"></i></span>
          <div>
            <h4 class="font-weight-bold mb-0">Manage News &amp; Updates</h4>
            <p class="page-subtitle mb-0">
              Create, edit, and publish announcements shown on the dashboard.
            </p>
          </div>
        </div>
        <button type="button" class="btn btn-primary btn-sm mt-2 mt-md-0" @click="openCreate">
          <i class="bi bi-plus-lg mr-1"></i> Post update
        </button>
      </div>
    </div>

    <div class="card shadow-sm border-0">
      <div class="card-body">
        <div class="toolbar-row mb-3">
          <div class="search-wrap">
            <i class="bi bi-search"></i>
            <input
              v-model.trim="searchQuery"
              type="text"
              class="form-control form-control-sm"
              placeholder="Search title or summary…"
            />
          </div>
          <select v-model="filterCategory" class="form-control form-control-sm filter-select">
            <option value="">All categories</option>
            <option
              v-for="option in categoryOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
          <select v-model="filterStatus" class="form-control form-control-sm filter-select">
            <option value="">All statuses</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>

        <div v-if="loading" class="text-center py-5 text-muted">
          <span class="spinner-border spinner-border-sm mr-2"></span>
          Loading postings…
        </div>

        <div v-else-if="error" class="text-center py-5 text-muted">
          <i class="bi bi-exclamation-circle d-block mb-2"></i>
          {{ error }}
        </div>

        <div v-else-if="!filteredUpdates.length" class="text-center py-5 text-muted">
          <i class="bi bi-inbox d-block mb-2" style="font-size: 1.5rem; opacity: 0.5"></i>
          <p class="mb-0">No postings match your filters.</p>
        </div>

        <div v-else class="table-responsive">
          <table class="table table-hover manage-table mb-0">
            <thead>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Status</th>
                <th>Published</th>
                <th>Author</th>
                <th class="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in filteredUpdates" :key="item.id">
                <td>
                  <button type="button" class="title-link" @click="openView(item)">
                    {{ item.title }}
                  </button>
                  <small v-if="item.summary" class="d-block text-muted row-summary">
                    {{ item.summary }}
                  </small>
                </td>
                <td>
                  <span class="cat-pill" :class="'cat-' + item.category">
                    {{ categoryLabel(item.category) }}
                  </span>
                </td>
                <td>
                  <span class="status-pill" :class="item.is_published ? 'is-published' : 'is-draft'">
                    {{ item.is_published ? "Published" : "Draft" }}
                  </span>
                </td>
                <td>{{ formatUpdateDate(item.published_at || item.created_at) }}</td>
                <td>{{ item.author_name }}</td>
                <td class="text-right text-nowrap">
                  <button
                    type="button"
                    class="btn btn-light btn-sm"
                    title="View"
                    @click="openView(item)"
                  >
                    <i class="bi bi-eye"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-light btn-sm"
                    title="Edit"
                    @click="openEdit(item)"
                  >
                    <i class="bi bi-pencil"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-light btn-sm"
                    title="Delete"
                    @click="confirmDelete(item)"
                  >
                    <i class="bi bi-trash text-danger"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          v-if="meta.total > meta.per_page"
          class="d-flex align-items-center justify-content-between pt-3 mt-2 border-top"
        >
          <small class="text-muted">
            Showing {{ rangeFrom }}–{{ rangeTo }} of {{ meta.total }}
          </small>
          <div class="btn-group btn-group-sm">
            <button
              type="button"
              class="btn btn-light"
              :disabled="meta.current_page <= 1"
              @click="goToPage(meta.current_page - 1)"
            >
              Previous
            </button>
            <button
              type="button"
              class="btn btn-light"
              :disabled="meta.current_page >= meta.last_page"
              @click="goToPage(meta.current_page + 1)"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- View modal -->
    <div class="modal fade" id="news-manage-view-modal" tabindex="-1" role="dialog" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
        <div class="modal-content form-modal">
          <div class="modal-header border-0 pb-0">
            <div class="flex-grow-1 pr-2">
              <span
                v-if="selectedUpdate"
                class="cat-pill"
                :class="'cat-' + selectedUpdate.category"
              >
                {{ categoryLabel(selectedUpdate.category) }}
              </span>
              <h5 class="modal-title font-weight-bold mt-2 mb-1">
                {{ selectedUpdate ? selectedUpdate.title : "" }}
              </h5>
              <small v-if="selectedUpdate" class="text-muted">
                {{ selectedUpdate.author_name }}
                · {{ formatUpdateDate(selectedUpdate.published_at || selectedUpdate.created_at, true) }}
                · {{ selectedUpdate.is_published ? "Published" : "Draft" }}
              </small>
            </div>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body pt-3">
            <p
              v-if="selectedUpdate && selectedUpdate.summary && !isForInformationCategory(selectedUpdate.category)"
              class="view-summary"
            >
              {{ selectedUpdate.summary }}
            </p>
            <div v-if="selectedUpdate" class="view-body">{{ selectedUpdate.body }}</div>
          </div>
          <div v-if="selectedUpdate" class="modal-footer border-0 pt-0">
            <button type="button" class="btn btn-light" data-dismiss="modal">Close</button>
            <button type="button" class="btn btn-primary" @click="openEditFromView">
              <i class="bi bi-pencil mr-1"></i> Edit posting
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Form modal -->
    <div class="modal fade" id="news-manage-form-modal" tabindex="-1" role="dialog" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
        <div class="modal-content form-modal">
          <div class="modal-header border-0 pb-0">
            <div>
              <h5 class="modal-title font-weight-bold mb-1">
                {{ editingId ? "Edit posting" : "New posting" }}
              </h5>
              <p class="text-muted small mb-0">Published posts appear on the dashboard (latest 3).</p>
            </div>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body pt-3">
            <div class="form-group">
              <label>Title <span class="text-danger">*</span></label>
              <input v-model.trim="form.title" type="text" class="form-control" maxlength="255" />
            </div>
            <div class="form-row">
              <div class="form-group col-md-6">
                <label>Category</label>
                <select v-model="form.category" class="form-control">
                  <option
                    v-for="option in categoryOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </div>
              <div class="form-group col-md-6">
                <label>Publish date</label>
                <input v-model="form.published_at" type="date" class="form-control" />
              </div>
            </div>
            <div v-if="!isForInformationForm" class="form-group">
              <label>Short summary</label>
              <textarea
                v-model.trim="form.summary"
                class="form-control"
                rows="2"
                maxlength="500"
                placeholder="Shown in the dashboard list"
              ></textarea>
            </div>
            <div class="form-group">
              <label>
                {{ isForInformationForm ? "Content" : "Full content" }}
                <span class="text-danger">*</span>
              </label>
              <textarea
                v-model.trim="form.body"
                class="form-control"
                rows="8"
                :placeholder="
                  isForInformationForm
                    ? 'Write the full information notice'
                    : 'Full announcement or release note'
                "
              ></textarea>
              <small v-if="isForInformationForm" class="text-muted d-block mt-1">
                A short summary for the dashboard list is generated automatically from this content.
              </small>
            </div>
            <div class="custom-control custom-checkbox mb-2">
              <input
                id="manage-news-published"
                v-model="form.is_published"
                type="checkbox"
                class="custom-control-input"
              />
              <label class="custom-control-label" for="manage-news-published">
                Published (visible on dashboard)
              </label>
            </div>
            <div v-if="form.is_published" class="custom-control custom-checkbox">
              <input
                id="manage-news-notify"
                v-model="form.notify_all_employees"
                type="checkbox"
                class="custom-control-input"
              />
              <label class="custom-control-label" for="manage-news-notify">
                Notify all CHED employees
              </label>
              <small class="text-muted d-block mt-1 ml-4">
                Sends a clickable in-app notification to every CHED employee.
              </small>
            </div>
          </div>
          <div class="modal-footer border-0 pt-0">
            <button type="button" class="btn btn-light" data-dismiss="modal">Cancel</button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="saving || !form.title || !form.body"
              @click="saveUpdate"
            >
              <span v-if="saving" class="spinner-border spinner-border-sm mr-1"></span>
              {{ editingId ? "Save changes" : "Publish" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { enableModalBlur, disableModalBlur } from "../../js/modal-blur";
import {
  categoryLabel,
  emptyUpdateForm,
  formatUpdateDate,
  generateSummaryFromBody,
  isForInformationCategory,
  UPDATE_CATEGORY_OPTIONS,
} from "../../js/news-updates";

export default {
  name: "NewsUpdatesManage",
  data() {
    return {
      updates: [],
      meta: {
        current_page: 1,
        per_page: 15,
        total: 0,
        last_page: 1,
      },
      loading: false,
      error: "",
      saving: false,
      searchQuery: "",
      filterCategory: "",
      filterStatus: "",
      selectedUpdate: null,
      editingId: null,
      form: emptyUpdateForm(),
    };
  },
  computed: {
    categoryOptions() {
      return UPDATE_CATEGORY_OPTIONS;
    },
    isForInformationForm() {
      return isForInformationCategory(this.form.category);
    },
    filteredUpdates() {
      let list = this.updates;

      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        list = list.filter(
          (item) =>
            String(item.title || "").toLowerCase().includes(q) ||
            String(item.summary || "").toLowerCase().includes(q)
        );
      }

      if (this.filterCategory) {
        list = list.filter((item) => item.category === this.filterCategory);
      }

      if (this.filterStatus === "published") {
        list = list.filter((item) => item.is_published);
      } else if (this.filterStatus === "draft") {
        list = list.filter((item) => !item.is_published);
      }

      return list;
    },
    rangeFrom() {
      if (!this.meta.total) return 0;
      return (this.meta.current_page - 1) * this.meta.per_page + 1;
    },
    rangeTo() {
      return Math.min(this.meta.current_page * this.meta.per_page, this.meta.total);
    },
  },
  methods: {
    categoryLabel,
    formatUpdateDate,
    isForInformationCategory,
    fetchUpdates(page = 1) {
      this.loading = true;
      this.error = "";

      return axios
        .get("api/system-updates", {
          params: { all: 1, per_page: 15, page },
        })
        .then((res) => {
          this.updates = (res.data && res.data.data) || [];
          this.meta = (res.data && res.data.meta) || this.meta;
        })
        .catch(() => {
          this.error = "Unable to load postings.";
        })
        .finally(() => {
          this.loading = false;
        });
    },
    goToPage(page) {
      if (page < 1 || page > this.meta.last_page) return;
      this.fetchUpdates(page);
    },
    openView(item) {
      this.selectedUpdate = item;
      $("#news-manage-view-modal").modal("show");
    },
    openCreate() {
      this.editingId = null;
      this.form = emptyUpdateForm();
      $("#news-manage-form-modal").modal("show");
    },
    openEdit(item) {
      this.editingId = item.id;
      this.form = {
        title: item.title || "",
        summary: item.summary || "",
        body: item.body || "",
        category: item.category || "announcement",
        is_published: item.is_published !== false,
        notify_all_employees: false,
        published_at: item.published_at
          ? String(item.published_at).slice(0, 10)
          : new Date().toISOString().slice(0, 10),
      };
      $("#news-manage-view-modal").modal("hide");
      this.$nextTick(() => {
        $("#news-manage-form-modal").modal("show");
      });
    },
    openEditFromView() {
      if (!this.selectedUpdate) return;
      this.openEdit(this.selectedUpdate);
    },
    saveUpdate() {
      if (!this.form.title || !this.form.body) return;

      this.saving = true;
      const payload = { ...this.form };
      if (isForInformationCategory(payload.category)) {
        payload.summary = generateSummaryFromBody(payload.body);
      }
      const request = this.editingId
        ? axios.put(`api/system-updates/${this.editingId}`, payload)
        : axios.post("api/system-updates", payload);

      request
        .then((res) => {
          toastr.success(
            (res.data && res.data.message) ||
              (this.editingId ? "Posting saved." : "Posting published.")
          );
          $("#news-manage-form-modal").modal("hide");
          this.fetchUpdates(this.meta.current_page);
        })
        .catch((err) => {
          const message =
            (err.response && err.response.data && err.response.data.message) ||
            "Failed to save posting.";
          toastr.error(message, "Error");
        })
        .finally(() => {
          this.saving = false;
        });
    },
    confirmDelete(item) {
      if (!confirm(`Remove "${item.title}"? This cannot be undone.`)) return;

      axios
        .delete(`api/system-updates/${item.id}`)
        .then((res) => {
          toastr.success((res.data && res.data.message) || "Posting removed.");
          $("#news-manage-view-modal").modal("hide");
          this.fetchUpdates(this.meta.current_page);
        })
        .catch(() => {
          toastr.error("Failed to remove posting.", "Error");
        });
    },
    bindModalBlur() {
      ["news-manage-view-modal", "news-manage-form-modal"].forEach((id) => {
        const $modal = $(`#${id}`);
        $modal.on("shown.bs.modal", enableModalBlur);
        $modal.on("hidden.bs.modal", disableModalBlur);
      });
    },
    unbindModalBlur() {
      ["news-manage-view-modal", "news-manage-form-modal"].forEach((id) => {
        const $modal = $(`#${id}`);
        $modal.off("shown.bs.modal", enableModalBlur);
        $modal.off("hidden.bs.modal", disableModalBlur);
      });
      disableModalBlur();
    },
  },
  mounted() {
    this.fetchUpdates();
    this.bindModalBlur();
  },
  beforeDestroy() {
    this.unbindModalBlur();
  },
};
</script>

<style scoped>
.page-head,
.card {
  border-radius: 14px !important;
}

.head-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(124, 92, 255, 0.12);
  color: #7c5cff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  margin-right: 1rem;
  flex-shrink: 0;
}

.page-subtitle {
  color: #768192;
  font-size: 0.9rem;
  margin-top: 0.15rem;
}

.toolbar-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  align-items: center;
}

.search-wrap {
  position: relative;
  flex: 1;
  min-width: 200px;
}

.search-wrap i {
  position: absolute;
  left: 0.65rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9aa5b5;
  font-size: 0.85rem;
}

.search-wrap input {
  padding-left: 2rem;
}

.filter-select {
  width: auto;
  min-width: 140px;
}

.manage-table th {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.35px;
  color: #768192;
  border-top: none;
}

.title-link {
  border: none;
  background: none;
  padding: 0;
  font-weight: 600;
  color: #15263f;
  text-align: left;
  cursor: pointer;
}

.title-link:hover {
  color: #7c5cff;
  text-decoration: underline;
}

.row-summary {
  font-size: 0.8rem;
  margin-top: 0.15rem;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.cat-pill {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
}

.cat-changelog {
  background: rgba(43, 182, 163, 0.14);
  color: #1f8f7f;
}

.cat-announcement {
  background: rgba(124, 92, 255, 0.14);
  color: #7c5cff;
}

.cat-maintenance {
  background: rgba(224, 144, 31, 0.14);
  color: #c97812;
}

.cat-update {
  background: rgba(77, 141, 255, 0.14);
  color: #3d7ae8;
}

.cat-for_information {
  background: rgba(99, 111, 131, 0.14);
  color: #636f83;
}

.status-pill {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
}

.is-published {
  background: rgba(43, 182, 163, 0.12);
  color: #1f8f7f;
}

.is-draft {
  background: rgba(118, 129, 146, 0.15);
  color: #768192;
}

.form-modal {
  border-radius: 14px;
  overflow: hidden;
}

.view-summary {
  padding: 0.85rem 1rem;
  border-radius: 10px;
  background: #f4f7fb;
  color: #5c6b86;
  font-size: 0.92rem;
  line-height: 1.55;
}

.view-body {
  color: #2b3a55;
  font-size: 0.95rem;
  line-height: 1.7;
  white-space: pre-wrap;
}
</style>
